import { gql } from "@apollo/client";

// Account/User related fragments
export const ACCOUNT_BASIC_INFO = gql`
  fragment AccountBasicInfo on Account {
    id
    firstName
    lastName
    deletedAt
    timeZone
    summary
    leadType
    Id_Ehr
    contactPhoneNumber
    activityPhoneNumber
    bdlProgramStartOn
    bdlProgramEndOn
    latest_program_type
    latest_program_type_status
    programStartOn
    programEndOn
  }
`;

export const SPECIALIST_INFO = gql`
  fragment SpecialistInfo on User {
    id
    firstName
    lastName
    email
  }
`;

// Playlist related fragments
export const PLAYLIST_BASIC_INFO = gql`
  fragment PlaylistBasicInfo on Playlist {
    id
    title
    startOn
    endOn
    isActive
  }
`;

export const PLAYLIST_WITH_PROGRAM = gql`
  fragment PlaylistWithProgram on Playlist {
    ...PlaylistBasicInfo
    program {
      id
    }
  }
  ${PLAYLIST_BASIC_INFO}
`;

export const VOICE_MESSAGE_DETAILED = gql`
  fragment VoiceMessageDetailed on VoiceMessage {
    id
    title
    feature
    templateLink
    focus
    cognitiveDomains
    audioFile {
      id
      durationInSeconds
    }
  }
`;

export const PLAYLIST_VOICE_MESSAGE_DETAILED = gql`
  fragment PlaylistVoiceMessageDetailed on PlaylistVoiceMessage {
    id
    order
    createdOn
    updatedOn
    playlist {
      id
      title
    }
    voiceMessage {
      ...VoiceMessageDetailed
    }
  }
  ${VOICE_MESSAGE_DETAILED}
`;

export const PLAYLIST_DETAILED = gql`
  fragment PlaylistDetailed on Playlist {
    id
    title
    isActive
    startOn
    endOn
    createdOn
    updatedOn
    deletedAt
    program {
      id
      round
      program_type
      start_date
      end_date
    }
    playlistVoiceMessages {
      ...PlaylistVoiceMessageDetailed
    }
  }
  ${PLAYLIST_VOICE_MESSAGE_DETAILED}
`;

// Call record related fragments
export const CALL_RECORD_BASIC = gql`
  fragment CallRecordBasic on CallRecord {
    sid
    durationInSeconds
    outcome
    type
    createdOn
    recordingDownloadUrl
    programRating
    fullProgramRatingResponse
    reflectionOnLearnings
    errorDetails
    hangupDetails
    hbacalldata
    sessionCount
    programWeek
  }
`;

// Complete account info with all relations
export const ACCOUNT_FULL_INFO = gql`
  fragment AccountFullInfo on Account {
    ...AccountBasicInfo
    specialist {
      ...SpecialistInfo
    }
    playlists {
      ...PlaylistWithProgram
    }
    latestProgram {
      id
      program_type
      start_date
      end_date
    }
  }
  ${ACCOUNT_BASIC_INFO}
  ${SPECIALIST_INFO}
  ${PLAYLIST_WITH_PROGRAM}
`;

// Treatment/Program related fragments
export const PROGRAM_HISTORY_INFO = gql`
  fragment ProgramHistoryInfo on ProgramHistory {
    id
    program_type
    start_date
    end_date
    round
    created_on
    updated_at
    status
  }
`;

export const PROGRAM_HISTORY_WITH_SPECIALIST = gql`
  fragment ProgramHistoryWithSpecialist on ProgramHistory {
    ...ProgramHistoryInfo
    specialist {
      ...SpecialistInfo
    }
  }
  ${PROGRAM_HISTORY_INFO}
  ${SPECIALIST_INFO}
`;

export const CALL_PLAN_INFO = gql`
  fragment CallPlanInfo on CallPlan {
    id
    programWeek
    startDate
    endDate
    isActive
    program {
      id
      round
      program_type
      start_date
      end_date
      status
    }
    activityCount
    hasDeliveries
  }
`;

/** Full call plan shape for view / edit modals and removeInteractions response. */
export const CALL_PLAN_DETAIL = gql`
  fragment CallPlanDetail on CallPlan {
    id
    programWeek
    startDate
    endDate
    hasDeliveries
    isActive
    program {
      id
      round
      program_type
      start_date
      end_date
    }
    interactions {
      id
      order
      skippable
      type
      ... on OtherInteraction {
        contentText
      }
      ... on ActivityInteraction {
        activityId
        title
        description
        estimatedDuration
        contents {
          id
          sequenceOrder
          readableText
          ... on QuestionActivityContent {
            questionTypeLabel
            responseTimeLimitSec
            silenceBeforeResponseSec
            expectedAnswers {
              id
              expectedAnswerText
            }
          }
        }
        createdBy {
          id
          firstName
          lastName
        }
        voiceMessage {
          id
          audioFile {
            durationInSeconds
          }
        }
      }
    }
  }
`;

// Call schedule related fragments
export const CALL_SCHEDULE_INFO = gql`
  fragment CallScheduleInfo on CallSchedule {
    id
    cronExpression
    scheduleDescription
    callFlowVersion
  }
`;

// Get single user specific fragments
export const GET_SINGLE_USER_ACCOUNT = gql`
  fragment GetSingleUserAccount on Account {
    id
    firstName
    lastName
    email
    isTestUser
    contactPhoneNumber
    activityPhoneNumber
    timeZone
    summary
    programStartOn
    programEndOn
    bdlProgramStartOn
    bdlProgramEndOn
    createdOn
    deletedAt
    profilePhotoUrl
    leadType
    Id_Ehr
    latest_program_type
    callDeliverySpeed
    specialist {
      id
      firstName
    }
    playlists {
      ...PlaylistDetailed
    }
    callPlans {
      ...CallPlanInfo
    }
    callRecords {
      sid
      activities {
        id
        callSid
        voiceMessage {
          id
        }
      }
    }
    callSchedules {
      ...CallScheduleInfo
    }
  }
  ${PLAYLIST_DETAILED}
  ${CALL_SCHEDULE_INFO}
  ${CALL_PLAN_INFO}
`;
